"use client"

import { useState, useEffect } from "react"
import { useTranslation } from "react-i18next"

const CelebrateModal = ({ isOpen, onClose, onPost, onMaybeLater, petImagePreview, petName, textPost, setTextPost, isPosting = false, userTypeColor = "#8548A8" }) => {
  const { t } = useTranslation();
  const [message, setMessage] = useState(`🎉 Meet ${petName} the newest member of our family! Excited to welcome this adorable pet and start a new chapter of love, joy, and unforgettable memories together. 🐾`);

  // Scroll to top when modal opens
  useEffect(() => {
    if (isOpen) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [isOpen]);

  if (!isOpen) return null

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        fontFamily: '"Red Hat Display", sans-serif',
      }}
    >
      <style>
        {`
        @keyframes float-bounce {
          0% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0); }
        }
        .celebrate-image {
          animation: float-bounce 3s ease-in-out infinite;
        }
        `}
      </style>
      <div
        style={{
          background: "#FFFFFF",
          borderRadius: "20px",
          padding: "40px",
          width: "100%",
          maxWidth: "560px",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
          textAlign: "center",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "24px",
        }}
      >
        {/* Celebration Image */}
        <img
          src={petImagePreview || "/images/no-image-pet.png"}
          alt={t("addPet.celebrateModal.imageAlt")}
          className="celebrate-image"
          style={{
            width: "150px",
            height: "120px",
            objectFit: "contain",
            marginBottom: "16px",
          }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "/images/no-image-pet.png";
          }}
        />

        <h2
          style={{
            fontSize: "28px",
            fontWeight: 600,
            color: "#1F2937",
            margin: 0,
          }}
        >
          {t("addPet.celebrateModal.title", { petName })}
        </h2>
        <p
          style={{
            fontSize: "14px",
            color: "#6B7280",
            margin: 0,
            marginBottom: "8px",
            fontStyle: "italic",
          }}
        >
          {t("addPet.celebrateModal.editHint")}
        </p>
        <textarea
          value={textPost}
          onChange={e => setTextPost(e.target.value)}
          style={{
            width: "100%",
            minHeight: "100px",
            fontSize: "16px",
            color: "#494949",
            margin: 0,
            lineHeight: "1.5",
            borderRadius: "10px",
            border: "1px solid #E5E7EB",
            padding: "12px",
            resize: "none",
            fontFamily: '"Red Hat Display", sans-serif',
            boxSizing: "border-box",
          }}
          placeholder={message}
        />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            width: "100%",
            marginTop: "20px",
            alignItems: "center",
          }}
        >
          <button
            onClick={onPost}
            disabled={isPosting}
            style={{
              width: "280px",
              height: "56px",
              background: userTypeColor,
              color: "#FFFFFF",
              border: "none",
              borderRadius: "14px",
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: "18px",
              fontWeight: 600,
              cursor: isPosting ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              opacity: isPosting ? 0.7 : 1,
            }}
            onMouseEnter={(e) => {
              if (isPosting) return;
              // Calculate darker shade for hover
              const hoverColor = userTypeColor === "#8548A8" ? "#7A4397" :
                userTypeColor === "#00D4B3" ? "#00C4A7" :
                  userTypeColor === "#D65745" ? "#C7493A" :
                    userTypeColor === "#2A8AD2" ? "#2596BE" : userTypeColor;
              e.target.style.background = hoverColor;
              e.target.style.transform = "translateY(-1px)"
              e.target.style.boxShadow = "0 6px 20px rgba(0,0,0,0.15)"
            }}
            onMouseLeave={(e) => {
              if (isPosting) return;
              e.target.style.background = userTypeColor;
              e.target.style.transform = "translateY(0)"
              e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"
            }}
          >
            {isPosting ? t("addPet.celebrateModal.postingLabel") || "Uploading..." : t("addPet.celebrateModal.shareButton")}
          </button>
          <button
            onClick={onMaybeLater}
            style={{
              width: "280px",
              height: "56px",
              background: "#FFFFFF",
              color: "#303E4E",
              border: "1px solid #49494966",
              borderRadius: "14px",
              fontFamily: '"Red Hat Display", sans-serif',
              fontSize: "18px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              // Use user type color for hover border and text
              const hoverBg = userTypeColor === "#8548A8" ? "#F8F4FF" :
                userTypeColor === "#00D4B3" ? "#E0F7F4" :
                  userTypeColor === "#D65745" ? "#FEF2F2" :
                    userTypeColor === "#2A8AD2" ? "#EFF6FF" : "#F8F4FF";
              e.target.style.background = hoverBg;
              e.target.style.borderColor = userTypeColor;
              e.target.style.color = userTypeColor;
              e.target.style.transform = "translateY(-1px)"
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "#FFFFFF"
              e.target.style.borderColor = "#49494966"
              e.target.style.color = "#303E4E"
              e.target.style.transform = "translateY(0)"
            }}
          >
            {t("addPet.celebrateModal.maybeLaterButton")}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CelebrateModal
